import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { MagnifyingGlass, Plus, Funnel, ArrowsClockwise, Trash, Check, X as XIcon, CaretUp, CaretDown, Info, PaperPlaneTilt } from '@phosphor-icons/react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { useSaaSAuth } from '../contexts/SaaSAuthContext';
import { featureGate, formatRemaining, quotaGate } from '../lib/entitlements';

const STATUS_COLORS = {
  new: 'bg-blue-50 text-blue-700 border-blue-200',
  processing: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  needs_review: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  exported: 'bg-purple-50 text-purple-700 border-purple-200',
  pushed_to_campaign: 'bg-green-50 text-green-800 border-green-300',
  do_not_contact: 'bg-gray-100 text-gray-600 border-gray-300',
};

const CREATE_NEW_GROUP = '__create_new_group__';
const GROUP_CREATION_COPY = {
  en: {
    option: 'Create a new Pro-Email group',
    label: 'New group name',
    placeholder: 'e.g. Potential Pro-Email Clients',
    hint: 'The group will be created in Pro-Email and the selected contacts will be added immediately.',
    confirm: 'Create group and push',
  },
  de: {
    option: 'Neue Pro-Email-Gruppe erstellen',
    label: 'Name der neuen Gruppe',
    placeholder: 'z. B. Potenzielle Pro-Email-Kunden',
    hint: 'Die Gruppe wird in Pro-Email erstellt und die ausgewählten Kontakte werden sofort hinzugefügt.',
    confirm: 'Gruppe erstellen und übertragen',
  },
  es: {
    option: 'Crear un nuevo grupo de Pro-Email',
    label: 'Nombre del nuevo grupo',
    placeholder: 'p. ej., Clientes potenciales de Pro-Email',
    hint: 'El grupo se creará en Pro-Email y los contactos seleccionados se añadirán inmediatamente.',
    confirm: 'Crear grupo y enviar',
  },
};

export default function CompaniesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSaaSAuth();
  const pushFeature = featureGate(user, 'has_pro_email_integration');
  const pushQuota = quotaGate(user, 'pushed_leads', 'max_pushed_leads');
  const pushBlocked = !pushFeature.allowed || !pushQuota.allowed;
  const pushBlockLabel = !pushFeature.allowed ? pushFeature.label : pushQuota.label;
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [showAdd, setShowAdd] = useState(false);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [industryFilter, setIndustryFilter] = useState('');
  const [industries, setIndustries] = useState([]);
  const [showPushDialog, setShowPushDialog] = useState(false);
  const [proEmailGroups, setProEmailGroups] = useState([]);
  const [destinationGroupId, setDestinationGroupId] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [newCompany, setNewCompany] = useState({ company_name: '', website_url: '', city: '', region: '', country: '', industry_raw: '', phone: '' });
  const locale = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const groupCreationCopy = GROUP_CREATION_COPY[locale] || GROUP_CREATION_COPY.en;
  const creatingNewGroup = destinationGroupId === CREATE_NEW_GROUP;

  // Load distinct industries for the filter dropdown
  useEffect(() => {
    api.get('/admin/dashboard').then(r => {
      const breakdown = r.data?.industry_breakdown || [];
      setIndustries(breakdown.map(b => b.industry).filter(Boolean));
    }).catch(() => {});
  }, []);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50, sort_by: sortBy, sort_order: sortOrder };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (industryFilter) params.industry = industryFilter;
      const { data } = await api.get('/companies', { params });
      setCompanies(data.items);
      setTotal(data.total);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, industryFilter, sortBy, sortOrder]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const SortHeader = ({ field, children }) => (
    <th
      className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left cursor-pointer hover:text-gray-900 select-none transition-colors"
      onClick={() => toggleSort(field)}
    >
      <span className="flex items-center gap-1">
        {children}
        {sortBy === field && (sortOrder === 'asc' ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />)}
      </span>
    </th>
  );

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === companies.length) setSelected(new Set());
    else setSelected(new Set(companies.map(c => c.id)));
  };

  const bulkAction = async (action) => {
    if (selected.size === 0) return;
    const confirmMsg = {
      approve: `Approve ${selected.size} companies? They will become eligible for export and Pro-Email push.`,
      reject: `Reject ${selected.size} companies? They will be excluded from exports.`,
      delete: `Permanently delete ${selected.size} companies and their contacts? This cannot be undone.`,
    };
    if (!window.confirm(confirmMsg[action] || `Apply "${action}" to ${selected.size} companies?`)) return;
    try {
      await api.post('/companies/bulk-action', { company_ids: [...selected], action });
      const hints = {
        approve: 'Approved! These leads are now ready for export or Pro-Email push.',
        reject: 'Rejected. These leads will not appear in campaign exports.',
        delete: 'Deleted successfully.',
      };
      toast.success(hints[action] || `${action} applied to ${selected.size} companies`);
      setSelected(new Set());
      fetchCompanies();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openPushDialog = async () => {
    if (selected.size === 0) return;
    if (pushBlocked) {
      toast.error(pushBlockLabel);
      return;
    }
    setShowPushDialog(true);
    setLoadingGroups(true);
    setProEmailGroups([]);
    setDestinationGroupId('');
    setNewGroupName('');
    try {
      const { data } = await api.get('/integrations/pro-email/groups');
      const groups = data.items || [];
      setProEmailGroups(groups);
    } catch (err) {
      toast.error(formatApiError(err) || t('workspace.companies.pushDialog.loadError'));
    } finally {
      setLoadingGroups(false);
    }
  };

  const pushToProEmail = async () => {
    if (selected.size === 0 || !destinationGroupId || pushing) return;
    if (creatingNewGroup && !newGroupName.trim()) return;
    setPushing(true);
    try {
      let resolvedGroupId = destinationGroupId;
      let resolvedGroupName = proEmailGroups.find(group => group.id === destinationGroupId)?.name || '';
      if (creatingNewGroup) {
        const { data: createdGroup } = await api.post('/integrations/pro-email/groups', {
          name: newGroupName.trim(),
          description: 'Created from Lead Creator',
        });
        resolvedGroupId = createdGroup.id;
        resolvedGroupName = createdGroup.name;
      }

      const { data } = await api.post('/integrations/pro-email/push', {
        company_ids: [...selected],
        destination_group_id: resolvedGroupId,
        locale,
      });
      const msg = t('workspace.companies.pushDialog.result', {
        pushed: data.pushed || 0,
        failed: data.failed || 0,
        skipped: data.skipped || 0,
        group: data.group_name || resolvedGroupName,
      });
      if (data.failed > 0) {
        toast.error(msg);
      } else {
        toast.success(msg);
      }
      if (data.skipped_details?.length > 0) {
        data.skipped_details.forEach(s => toast.info(
          t('workspace.companies.pushDialog.skipped', { company: s.company_name || s.company_id, reason: s.reason }),
          { duration: 6000 },
        ));
      }
      setShowPushDialog(false);
      setSelected(new Set());
      fetchCompanies();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPushing(false);
    }
  };

  const addCompany = async () => {
    try {
      await api.post('/companies', newCompany);
      toast.success(t('workspace.companies.created'));
      setShowAdd(false);
      setNewCompany({ company_name: '', website_url: '', city: '', region: '', country: '', industry_raw: '', phone: '' });
      fetchCompanies();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const totalPages = Math.ceil(total / 50);

  return (
    <div data-testid="companies-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter text-gray-900">{t('workspace.companies.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('workspace.companies.subtitle', { count: total })}</p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="bg-black text-white hover:bg-gray-800" data-testid="add-company-btn">
          <Plus size={16} className="mr-2" /> {t('workspace.companies.add')}
        </Button>
      </div>

      {/* Search + Filters */}
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder={t('workspace.companies.search')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9 bg-white border-gray-300"
            data-testid="companies-search"
          />
        </div>
        <Select value={industryFilter || 'all'} onValueChange={(v) => { setIndustryFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-52 bg-white border-gray-300" data-testid="industry-filter">
            <SelectValue placeholder={t('workspace.companies.allIndustries')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('workspace.companies.allIndustries')}</SelectItem>
            {industries.map(ind => (
              <SelectItem key={ind} value={ind}>{ind}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter || 'all'} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-44 bg-white border-gray-300" data-testid="status-filter">
            <Funnel size={14} className="mr-2 text-gray-400" />
            <SelectValue placeholder={t('workspace.companies.allStatuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('workspace.companies.allStatuses')}</SelectItem>
            {['new', 'needs_review', 'approved', 'rejected', 'exported', 'pushed_to_campaign'].map((state) => <SelectItem key={state} value={state}>{t(`workspace.companies.states.${state}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Active filter pills */}
      {(industryFilter || statusFilter) && (
        <div className="flex gap-2 mb-3">
          {industryFilter && (
            <button onClick={() => { setIndustryFilter(''); setPage(1); }} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-mono bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors" data-testid="clear-industry-filter">
              Industry: {industryFilter} <XIcon size={12} weight="bold" />
            </button>
          )}
          {statusFilter && (
            <button onClick={() => { setStatusFilter(''); setPage(1); }} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-mono bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors" data-testid="clear-status-filter">
              Status: {statusFilter.replace(/_/g, ' ')} <XIcon size={12} weight="bold" />
            </button>
          )}
          <button onClick={() => { setIndustryFilter(''); setStatusFilter(''); setSearch(''); setPage(1); }} className="text-xs text-gray-500 hover:text-gray-900 underline">
            Clear all
          </button>
        </div>
      )}

      {/* Table */}
      <div className="border border-gray-200 rounded-md bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="w-10 py-3 px-4">
                  <Checkbox checked={selected.size === companies.length && companies.length > 0} onCheckedChange={toggleAll} data-testid="select-all-checkbox" />
                </th>
                <SortHeader field="company_name">{t('workspace.companies.company')}</SortHeader>
                <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">{t('workspace.companies.domain')}</th>
                <SortHeader field="industry_raw">{t('workspace.companies.industry')}</SortHeader>
                <SortHeader field="city">{t('workspace.companies.location')}</SortHeader>
                <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left cursor-help">{t('workspace.companies.score')}</th>
                <SortHeader field="contact_count">{t('workspace.companies.contacts')}</SortHeader>
                <SortHeader field="status">{t('workspace.companies.status')}</SortHeader>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="py-12 text-center text-gray-400">{t('workspace.companies.loading')}</td></tr>
              ) : companies.length === 0 ? (
                <tr><td colSpan="8" className="py-12 text-center">
                  <p className="text-gray-400 mb-2">{t('workspace.companies.empty')}</p>
                  <p className="text-xs text-gray-400">{t('workspace.companies.emptyHint')}</p>
                </td></tr>
              ) : companies.map(c => (
                <tr
                  key={c.id}
                  className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${selected.has(c.id) ? 'bg-blue-50/50' : ''}`}
                  onClick={() => navigate(`/app/companies/${c.id}`)}
                  data-testid={`company-row-${c.id}`}
                >
                  <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleSelect(c.id)} />
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm font-medium text-gray-900">{c.company_name}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm font-mono text-gray-600">{c.normalized_domain || '-'}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-gray-700">{c.industry_raw || '-'}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-gray-700">{[c.city, c.region].filter(Boolean).join(', ') || '-'}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-mono text-sm font-bold text-gray-900">{c.score || 0}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-mono text-sm text-gray-600">{c.contact_count || 0}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono font-medium border ${STATUS_COLORS[c.status] || STATUS_COLORS.new}`}>
                      {t(`workspace.companies.states.${c.status}`, { defaultValue: c.status?.replace(/_/g, ' ') })}
                    </span>
                    {c.campaign_ready && <span className="ml-1.5 inline-flex w-2 h-2 rounded-full bg-emerald-500" title="Campaign ready" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-gray-500">{t('workspace.companies.page', { page, total: totalPages })}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>{t('workspace.companies.previous')}</Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>{t('workspace.companies.next')}</Button>
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white rounded-lg px-6 py-3 shadow-2xl flex items-center gap-4 z-50" data-testid="bulk-action-bar">
          <span className="text-sm font-mono">{t('workspace.companies.selected', { count: selected.size })}</span>
          <div className="w-px h-6 bg-gray-600" />
          <button onClick={() => bulkAction('approve')} className="flex items-center gap-1.5 text-sm hover:text-green-300 transition-colors" data-testid="bulk-approve-btn">
            <Check size={16} /> {t('workspace.companies.approve')}
          </button>
          <button onClick={() => bulkAction('reject')} className="flex items-center gap-1.5 text-sm hover:text-red-300 transition-colors" data-testid="bulk-reject-btn">
            <XIcon size={16} /> {t('workspace.companies.reject')}
          </button>
          <button onClick={() => bulkAction('delete')} className="flex items-center gap-1.5 text-sm hover:text-red-300 transition-colors" data-testid="bulk-delete-btn">
            <Trash size={16} /> {t('workspace.companies.delete')}
          </button>
          <div className="w-px h-6 bg-gray-600" />
          <button
            onClick={openPushDialog}
            disabled={pushBlocked}
            className="flex items-center gap-1.5 text-sm hover:text-blue-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="bulk-push-btn"
            title={pushBlocked ? pushBlockLabel : (pushQuota.remaining !== Number.POSITIVE_INFINITY ? `${formatRemaining(pushQuota.remaining)} pushes left` : undefined)}
          >
            <PaperPlaneTilt size={16} /> {pushBlocked ? pushBlockLabel : t('workspace.companies.push')}
          </button>
          <button onClick={() => setSelected(new Set())} className="text-sm text-gray-400 hover:text-white ml-2">{t('workspace.companies.clear')}</button>
        </div>
      )}

      <Dialog open={showPushDialog} onOpenChange={(open) => !pushing && setShowPushDialog(open)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('workspace.companies.pushDialog.title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <p className="text-sm text-gray-600">
              {t('workspace.companies.pushDialog.description', { count: selected.size })}
            </p>
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.1em] text-gray-500 mb-1 block">
                {t('workspace.companies.pushDialog.groupLabel')}
              </label>
              <Select value={destinationGroupId} onValueChange={setDestinationGroupId} disabled={loadingGroups || pushing}>
                <SelectTrigger data-testid="pro-email-group-select">
                  <SelectValue placeholder={loadingGroups
                    ? t('workspace.companies.pushDialog.loadingGroups')
                    : t('workspace.companies.pushDialog.groupPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {proEmailGroups.map(group => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name} ({group.member_count || 0})
                    </SelectItem>
                  ))}
                  <SelectItem value={CREATE_NEW_GROUP}>{groupCreationCopy.option}</SelectItem>
                </SelectContent>
              </Select>
              {!loadingGroups && proEmailGroups.length === 0 && (
                <p className="text-xs text-amber-700 mt-2" data-testid="pro-email-no-groups">
                  {t('workspace.companies.pushDialog.noGroups')}
                </p>
              )}
            </div>
            {creatingNewGroup && (
              <div className="rounded-md border border-blue-200 bg-blue-50 p-4 space-y-2">
                <label className="text-xs font-mono uppercase tracking-[0.1em] text-blue-800 block">
                  {groupCreationCopy.label}
                </label>
                <Input
                  value={newGroupName}
                  onChange={(event) => setNewGroupName(event.target.value)}
                  placeholder={groupCreationCopy.placeholder}
                  disabled={pushing}
                  maxLength={200}
                  data-testid="new-pro-email-group-name"
                />
                <p className="text-xs text-blue-700">{groupCreationCopy.hint}</p>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowPushDialog(false)} disabled={pushing}>
                {t('workspace.companies.pushDialog.cancel')}
              </Button>
              <Button
                onClick={pushToProEmail}
                disabled={!destinationGroupId || (creatingNewGroup && !newGroupName.trim()) || loadingGroups || pushing || pushBlocked}
                className="bg-black text-white hover:bg-gray-800"
                data-testid="confirm-pro-email-push"
              >
                {pushing
                  ? t('workspace.companies.pushDialog.pushing')
                  : (pushBlocked
                    ? pushBlockLabel
                    : creatingNewGroup
                      ? groupCreationCopy.confirm
                      : t('workspace.companies.pushDialog.confirm'))}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Company Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('workspace.companies.add')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.1em] text-gray-500 mb-1 block">{t('workspace.companies.name')}</label>
              <Input value={newCompany.company_name} onChange={e => setNewCompany(p => ({...p, company_name: e.target.value}))} data-testid="new-company-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.1em] text-gray-500 mb-1 block">{t('workspace.companies.website')}</label>
                <Input value={newCompany.website_url} onChange={e => setNewCompany(p => ({...p, website_url: e.target.value}))} placeholder="https://..." data-testid="new-company-website" />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.1em] text-gray-500 mb-1 block">{t('workspace.companies.phone')}</label>
                <Input value={newCompany.phone} onChange={e => setNewCompany(p => ({...p, phone: e.target.value}))} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.1em] text-gray-500 mb-1 block">{t('workspace.companies.city')}</label>
                <Input value={newCompany.city} onChange={e => setNewCompany(p => ({...p, city: e.target.value}))} />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.1em] text-gray-500 mb-1 block">{t('workspace.companies.region')}</label>
                <Input value={newCompany.region} onChange={e => setNewCompany(p => ({...p, region: e.target.value}))} />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.1em] text-gray-500 mb-1 block">{t('workspace.companies.country')}</label>
                <Input value={newCompany.country} onChange={e => setNewCompany(p => ({...p, country: e.target.value}))} />
              </div>
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.1em] text-gray-500 mb-1 block">{t('workspace.companies.industry')}</label>
              <Input value={newCompany.industry_raw} onChange={e => setNewCompany(p => ({...p, industry_raw: e.target.value}))} placeholder="e.g. Audio Visual Integration" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowAdd(false)}>{t('workspace.companies.cancel')}</Button>
              <Button onClick={addCompany} className="bg-black text-white hover:bg-gray-800" data-testid="save-company-btn" disabled={!newCompany.company_name}>
                {t('workspace.companies.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
